import format from "date-fns/format";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { isMobile } from "react-device-detect";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { query } from "scmp-app/components/article/article-render/newsletter-article/issues";
import { DropdownSelect } from "scmp-app/components/common/dropdown-select";
import { page as ArticlePage } from "scmp-app/pages/article";
import type { issuesDropdownNewsletterArticleArticle$key } from "scmp-app/queries/__generated__/issuesDropdownNewsletterArticleArticle.graphql";
import type { issuesDropdownNewsletterArticleArticleConnection$key } from "scmp-app/queries/__generated__/issuesDropdownNewsletterArticleArticleConnection.graphql";
import type { issuesNewsletterArticleQuery } from "scmp-app/queries/__generated__/issuesNewsletterArticleQuery.graphql";

type Issue = {
  entityId: string;
  entityUuid: string;
  urlAlias: string;
};

type Props = {
  articleReference: issuesDropdownNewsletterArticleArticle$key;
  reference: PreloadedQuery<issuesNewsletterArticleQuery>;
};

export const NewsletterArticleIssuesDropdown: FunctionComponent<Props> = ({
  articleReference,
  reference,
}) => {
  const router = useRouter();
  const article = useFragment<issuesDropdownNewsletterArticleArticle$key>(
    graphql`
      fragment issuesDropdownNewsletterArticleArticle on Article {
        entityId
      }
    `,
    articleReference,
  );

  const queryReference = usePreloadedQuery<issuesNewsletterArticleQuery>(query, reference);

  const articles = useFragment<issuesDropdownNewsletterArticleArticleConnection$key>(
    graphql`
      fragment issuesDropdownNewsletterArticleArticleConnection on ArticleConnection {
        edges {
          node {
            entityId
            entityUuid
            publishedDate
            urlAlias
          }
        }
      }
    `,
    queryReference.articles,
  );

  const getOptions = () => {
    let options = articles.edges.map((issue, index) => ({
      isDefault: issue.node.entityId === article.entityId,
      isSelected: issue.node.entityId === article.entityId,
      label: `Issue: ${format(issue?.node?.publishedDate ?? new Date(), "d MMM yyyy")}`,
      value: `${index}`,
    }));
    // If no matching on default choice
    if (!options.some(option => option.isDefault)) {
      options = [
        {
          isDefault: true,
          isSelected: true,
          label: "Select Issue",
          value: "default",
        },
        ...options,
      ];
    }
    return options;
  };

  const handleSelect = (index: string) => {
    const issues: Issue[] = articles.edges.map(issue => ({
      entityId: issue.node.entityId,
      entityUuid: issue.node.entityUuid,
      urlAlias: issue.node.urlAlias,
    }));
    const selectedOption = issues[+index];
    if (selectedOption) {
      void router.push(
        ArticlePage.route({
          entityId: selectedOption.entityId,
          entityUuid: selectedOption.entityUuid,
        }),
        selectedOption.urlAlias,
      );
    }
  };

  return (
    <DropdownSelect isNativeSelect={isMobile} onChange={handleSelect} options={getOptions()} />
  );
};

NewsletterArticleIssuesDropdown.displayName = "NewsletterArticleIssuesDropdown";
